import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Stack,
  CircularProgress,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { VendorFormData } from "../../types/VendorFormData";
import { apiRequest } from "../../utils/apiClient";
import VendorDetailView from "./VendorDetailView";

const VendorReviewPage = () => {
  const [vendors, setVendors] = useState<VendorFormData[]>([]);
  const [loading, setLoading] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectingVendor, setRejectingVendor] = useState<string | null>(null);
  const [rejectNotes, setRejectNotes] = useState("");
  const [actionLoading, setActionLoading] = useState<string | null>(null);


  useEffect(() => {
    fetchPendingVendors();
  }, []);

  const fetchPendingVendors = async () => {
    setLoading(true);
    try {
      const res = await apiRequest("/api/vendors");
      const pending = res?.filter((v: VendorFormData) => v.status === "PENDING");
      setVendors(pending || []);
    } catch (err) {
      console.error("Error fetching vendors:", err);
    } finally {
      setLoading(false);
    }
  };

  const approveVendor = async (vendorName: string) => {
    setActionLoading(vendorName);
    try {
      await apiRequest(`/api/vendors/${vendorName}/approve`, { method: "PUT" });
      setVendors((prev) => prev.filter((v) => v.vendorName !== vendorName));
    } catch (err) {
      console.error(err);
    }
    finally {
    setActionLoading(null);
  }
  };

  const rejectVendor = async () => {
  if (!rejectingVendor) return;
  setActionLoading(rejectingVendor);
  try {
    await apiRequest(`/api/vendors/${rejectingVendor}/reject`, {
      method: "PUT",
      body: { notes: rejectNotes },
    });
    closeRejectDialog();
    await fetchPendingVendors(); // reload fresh data
  } catch (err) {
    console.error(err);
  } finally {
    setActionLoading(null);
  }
};


  const openRejectDialog = (vendorName: string) => {
    setRejectingVendor(vendorName);
    setRejectNotes("");
    setRejectDialogOpen(true);
  };

  const closeRejectDialog = () => {
    setRejectDialogOpen(false);
    setRejectingVendor(null);
    setRejectNotes("");
  };

  if (loading) return <CircularProgress />;
  if (vendors.length === 0) return <Typography className="p-4">No pending vendors.</Typography>;

  return (
    <div className="p-3 md:p-4">
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
        Pending Vendor Submissions
      </Typography>

      <Stack spacing={3}>
        {vendors.map((vendor) => (
          <Accordion key={vendor.vendorName} sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">{vendor.vendorName?.toUpperCase()}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <VendorDetailView vendor={vendor} />

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mt={3}>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={() => vendor.vendorName && approveVendor(vendor.vendorName)}
                disabled={!vendor.vendorName || actionLoading === vendor.vendorName}
              >
                {actionLoading === vendor.vendorName ? <CircularProgress size={20} color="inherit" /> : "Approve"}
              </Button>

              <Button
                variant="outlined"
                color="error"
                startIcon={<CancelIcon />}
                onClick={() => vendor.vendorName && openRejectDialog(vendor.vendorName)}
                disabled={!vendor.vendorName || actionLoading === vendor.vendorName}
              >
                {actionLoading === vendor.vendorName ? <CircularProgress size={20} color="inherit" /> : "Reject"}
              </Button>
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={closeRejectDialog}>
        <DialogTitle>Reject Vendor</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Reason for rejection"
            multiline
            minRows={3}
            value={rejectNotes}
            onChange={(e) => setRejectNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRejectDialog}>Cancel</Button>
          <Button variant="contained" color="error" onClick={rejectVendor}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default VendorReviewPage;
